import React, { FC, memo } from 'react'
import { Heading } from '@ucheba/ui/components/Heading/desktop'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import styled from 'styled-components'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { Button } from '@ucheba/ui/components/Button/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { sv } from '@ucheba/utils/helpers/styles/variables'
import { useRouter } from 'next/router'
import { stringifyUrl } from '@ucheba/utils/helpers/router'
import Link from 'next/link'
import { Picture } from '@ucheba/ui/components/Picture'
import { IProgramListProps } from '../types'
import SC from '../base/ProgramList.styles'
import { ProgramListItem } from './ProgramListItem'
import { getProgramType, getTextForSimilar, showAlsoButtonText, showButton } from '../bll'
import { forAbiturients } from '../../../routs'
import { EQueryParams } from '../../../pages/for-abiturients/planner/programs/types'

const { Block } = SC

const Image = styled(Picture)`
  max-height: 48px;
`

const LinkBlock = styled.div`
  padding-top: ${sv.spacing.large};
  border-top: 1px solid ${sv.color.gray9};
`

const ProgramList: FC<IProgramListProps> = (props) => {
  const {
    isButtonsIcon,
    items,
    selectedIds,
    offSelect,
    onSelect,
    showButtons,
    groupByVuz,
    institutions,
    showAlso,
    showLinkToOtherPrograms,
    handleDeleteUZ,
    toggleBtnToRemove,
    showMoreButton = true,
    showLogo = true,
    showExamsInProfile = true,
  } = props

  const router = useRouter()
  const programType = getProgramType()

  if (groupByVuz) {
    return (
      <Block>
        {institutions &&
          institutions.map((vuz, key) => (
            <React.Fragment key={vuz.id}>
              {key !== 0 && <Spacing spacing='ularge' />}

              <Grid
                nowrap
                justifyContent='space-between'
                spacing='xsmall'
                alignItems='center'
              >
                <GridCol width='auto' shrink='initial'>
                  <Link
                    href={`/uz/${vuz.id}`}
                    target='_blank'
                    style={{ color: 'inherit' }}
                  >
                    <Heading h3={vuz.name} spacing='null' />
                  </Link>
                </GridCol>

                <GridCol width='auto'>
                  {!!vuz?.logo?.fallbackUrl && (
                    <Link
                      href={`/uz/${vuz.id}`}
                      target='_blank'
                      style={{ color: 'inherit' }}
                    >
                      <Image
                        height={48}
                        alt='логотип учебного заведения'
                        fallbackUrl={vuz?.logo?.fallbackUrl}
                        webpUrl={vuz?.logo?.webp}
                      />
                    </Link>
                  )}
                </GridCol>
              </Grid>

              <Spacing spacing='large' />

              {vuz.programs.map((program) => (
                <ProgramListItem
                  isButtonsIcon={isButtonsIcon}
                  key={program.id}
                  selectedIds={selectedIds}
                  offSelect={offSelect}
                  onSelect={onSelect}
                  showButtons={showButtons}
                  groupByVuz={groupByVuz}
                  toggleBtnToRemove={toggleBtnToRemove}
                  showLogo={showLogo}
                  showExamsInProfile={showExamsInProfile}
                  {...program}
                />
              ))}

              {showLinkToOtherPrograms && (
                <LinkBlock>
                  <Grid justifyContent='space-between'>
                    <GridCol width='auto'>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ marginRight: '10px' }}>
                          <Text fontWeight='semibold'>
                            {getTextForSimilar(
                              vuz.programsPerEnrollee,
                              vuz.programsTotal,
                              vuz.programsSelectedTotal
                            )}
                          </Text>
                        </div>

                        <div style={{ flex: 1 }}>
                          {showButton(vuz.programsTotal, vuz.programsSelectedTotal) && (
                            <Button
                              view='link'
                              size='small'
                              onClick={() => {
                                router.push(
                                  stringifyUrl(forAbiturients.planner.programs.path, {
                                    update: { uz: vuz.id, [EQueryParams.isVuzGroup]: 1 },
                                  }),
                                  undefined,
                                  {
                                    scroll: true,
                                  }
                                )
                              }}
                            >
                              Подходящие программы
                            </Button>
                          )}
                        </div>
                      </div>
                    </GridCol>

                    <GridCol width='auto'>
                      <Button
                        view='link'
                        size='small'
                        onClick={() => {
                          if (handleDeleteUZ) {
                            const ids = vuz.programs.map((item) => item.id)
                            handleDeleteUZ([vuz.id], ids)
                          }
                        }}
                      >
                        {programType === '1001' ? 'Удалить колледж' : 'Удалить вуз'}
                      </Button>
                    </GridCol>
                  </Grid>
                </LinkBlock>
              )}

              {showMoreButton && vuz.programsTotal - vuz.programs.length > 0 && (
                <Button
                  view='outline'
                  color='blue'
                  block
                  onClick={() => {
                    const exclude = vuz.programs.map((program) => program.id)
                    if (showAlso) {
                      showAlso(vuz.id, exclude)
                    }
                  }}
                >
                  {showAlsoButtonText(vuz.programsTotal - vuz.programs.length)}
                </Button>
              )}
            </React.Fragment>
          ))}
      </Block>
    )
  }

  return (
    <Block>
      {items &&
        items.map((program) => (
          <ProgramListItem
            isButtonsIcon={isButtonsIcon}
            key={program.id}
            selectedIds={selectedIds}
            offSelect={offSelect}
            onSelect={onSelect}
            showButtons={showButtons}
            toggleBtnToRemove={toggleBtnToRemove}
            showLogo={showLogo}
            showExamsInProfile={showExamsInProfile}
            {...program}
          />
        ))}
    </Block>
  )
}

export { ProgramList }
export default memo(ProgramList)
